import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import LoanRepaymentForm from "./LoanRepaymentForm";

const money = (value, currency) =>
  `${Number(value || 0).toLocaleString()} ${currency || ""}`.trim();

const statusMeta = (row) => {
  const principal = Number(row.principal_amount || 0);
  const remaining = Number(row.amount_remaining || 0);

  if (remaining <= 0 || row.status === "PAID" || row.status === "REPAID") {
    return { label: "Remboursé", color: "success" };
  }
  if (principal > 0 && remaining < principal) {
    return { label: "Partiellement remboursé", color: "warning" };
  }
  if (row.status === "CANCELLED") {
    return { label: "Annulé", color: "default" };
  }
  return { label: "En cours", color: "primary" };
};

const LoanList = ({ refreshKey = 0 }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [repaymentLoanId, setRepaymentLoanId] = useState(null);

  const fetchRows = useCallback(async () => {
    if (!user?.agency_id) return;
    try {
      setLoading(true);
      const res = await api.get("/loans", {
        params: { agency_id: user.agency_id },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      enqueueSnackbar("Échec du chargement des prêts", { variant: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, enqueueSnackbar]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows, refreshKey]);

  const totalsByCurrency = useMemo(() => {
    return rows.reduce((acc, row) => {
      const currency = row.currency || "-";
      if (!acc[currency]) acc[currency] = { principal: 0, remaining: 0 };
      acc[currency].principal += Number(row.principal_amount || 0);
      acc[currency].remaining += Number(row.amount_remaining || 0);
      return acc;
    }, {});
  }, [rows]);

  const handleRepaymentSuccess = async () => {
    setRepaymentLoanId(null);
    await fetchRows();
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Portefeuille de prêts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Suivi du capital prêté, des remboursements partiels et des soldes à recouvrer.
          </Typography>
        </Box>

        {Object.keys(totalsByCurrency).length > 0 && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
            {Object.entries(totalsByCurrency).map(([currency, totals]) => (
              <Chip
                key={currency}
                variant="outlined"
                label={`${currency} • Restant ${Number(totals.remaining).toLocaleString()} / ${Number(totals.principal).toLocaleString()}`}
              />
            ))}
          </Stack>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">Aucun prêt enregistré.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Débiteur</TableCell>
                  <TableCell>Prêt</TableCell>
                  <TableCell>Remboursé</TableCell>
                  <TableCell>Reste</TableCell>
                  <TableCell>Progression</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Échéance</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const principal = Number(row.principal_amount || 0);
                  const remaining = Math.max(Number(row.amount_remaining || 0), 0);
                  const repaid = Math.max(principal - remaining, 0);
                  const progress = principal > 0 ? Math.min((repaid / principal) * 100, 100) : 0;
                  const status = statusMeta(row);
                  const canRepay = remaining > 0 && row.status !== "CANCELLED";
                  const isOpen = repaymentLoanId === row.id;

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {row.debtor_name || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.loan_date || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>{money(principal, row.currency)}</TableCell>
                        <TableCell>{money(repaid, row.currency)}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>
                          {money(remaining, row.currency)}
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          <Stack spacing={0.5}>
                            <LinearProgress variant="determinate" value={progress} />
                            <Typography variant="caption">{Math.round(progress)}%</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={status.label} color={status.color} variant="outlined" />
                        </TableCell>
                        <TableCell>{row.due_date || "-"}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant={isOpen ? "outlined" : "contained"}
                            disabled={!canRepay}
                            onClick={() => setRepaymentLoanId(isOpen ? null : row.id)}
                          >
                            {remaining <= 0 ? "Soldé" : isOpen ? "Fermer" : "Rembourser"}
                          </Button>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, borderBottom: isOpen ? undefined : 0 }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <LoanRepaymentForm
                              loan={row}
                              onSuccess={handleRepaymentSuccess}
                              onCancel={() => setRepaymentLoanId(null)}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Paper>
  );
};

export default LoanList;
